import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { api } from '../api/client';
import { useAuth } from '../stores/auth';
import { toast } from '../stores/toasts';
import { Modal } from '../components/Modal';

export function WorkshopsPage() {
  const auth = useAuth();
  const qc = useQueryClient();
  const [openCreate, setOpenCreate] = useState(false);
  const [name, setName] = useState('');
  const [city, setCity] = useState('Toshkent');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');

  const myWorkshops = auth.workshops;
  const { data: subData } = useQuery({
    queryKey: ['my-subscription'],
    queryFn: () => api.mySubscription(),
  });
  const sub = subData as
    | {
        hasWorkshop: boolean;
        workshop?: { plan: string } | null;
        subscription?: { status: string; endAt: string; daysRemaining: number } | null;
      }
    | undefined;
  const isOwner = sub?.workshop != null;
  const urgent = sub?.subscription && sub.subscription.status !== 'EXPIRED' && sub.subscription.daysRemaining <= 7;
  const expired = sub?.subscription?.status === 'EXPIRED';

  const create = useMutation({
    mutationFn: () =>
      api.post('/workshops', { name, city, phone, address }).then((r) => r),
    onSuccess: async () => {
      toast.success('Usta yaratildi');
      setOpenCreate(false);
      setName('');
      setPhone('');
      setAddress('');
      await qc.invalidateQueries({ queryKey: ['my-workshops'] });
      const list = await api.myWorkshops();
      auth.setWorkshops(list as any);
      const first = list[0] as any;
      if (first) {
        auth.setActiveWorkshop(first);
        try {
          await api.switchWorkshop(first.id);
        } catch {}
      }
    },
    onError: (e: any) => toast.error(e?.message || 'Xatolik'),
  });

  function submit() {
    if (!name.trim()) {
      toast.warning('Nom kiriting');
      return;
    }
    create.mutate();
  }

  return (
    <>
      <h3>🏢 Mening ustalarim</h3>

      {isOwner && (urgent || expired) && sub?.subscription && (
        <Link
          to="/subscription"
          className="card"
          style={{
            background: expired ? 'rgba(239, 68, 68, 0.1)' : 'rgba(245, 158, 11, 0.1)',
            border: `1px solid ${expired ? '#ef4444' : '#f59e0b'}`,
            textDecoration: 'none',
            color: 'inherit',
            display: 'block',
            marginBottom: 12,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {expired ? '⛔️ Tarif tugagan' : `⚠️ ${sub.subscription.daysRemaining} kun qoldi`}
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            {expired
              ? 'Yangi tarif rasmiylashtirish uchun admin bilan bog\'laning'
              : `Tugash: ${format(parseISO(sub.subscription.endAt), 'dd MMM yyyy')}`}
          </div>
        </Link>
      )}

      {myWorkshops.length === 0 ? (
        <div className="empty">
          <div className="emoji">🏢</div>
          <div>Hozircha usta yo'q</div>
          <button className="btn btn-primary" style={{ marginTop: 10 }} onClick={() => setOpenCreate(true)}>
            ➕ Yangi usta yaratish
          </button>
        </div>
      ) : (
        <>
          {myWorkshops.map((w) => (
            <div key={w.id} className="card">
              <div className="row-between">
                <div>
                  <div style={{ fontWeight: 600, fontSize: 16 }}>{w.name}</div>
                  <div className="muted" style={{ fontSize: 13 }}>{w.address ?? w.city ?? '—'}</div>
                  <div className="row" style={{ gap: 6, marginTop: 6 }}>
                    <span className="tag primary">{w.roleInWorkshop ?? '—'}</span>
                    {w.isVerified && <span className="tag success">✓ Tasdiqlangan</span>}
                    {w.plan === 'TRIAL' && <span className="tag warning">Trial</span>}
                  </div>
                </div>
                {auth.activeWorkshop?.id === w.id && <span className="tag success">Faol</span>}
              </div>
              {auth.activeWorkshop?.id !== w.id && (
                <button
                  className="btn btn-block"
                  style={{ marginTop: 10 }}
                  onClick={async () => {
                    try {
                      await api.switchWorkshop(w.id);
                      auth.setActiveWorkshop(w);
                      toast.success(`Faol usta: ${w.name}`);
                    } catch (e: any) {
                      toast.error(e?.message || 'Xatolik');
                    }
                  }}
                >
                  Faol qilish
                </button>
              )}
            </div>
          ))}
          <button className="btn btn-primary btn-block" style={{ marginTop: 10 }} onClick={() => setOpenCreate(true)}>
            ➕ Yana usta yaratish
          </button>
        </>
      )}

      <Modal
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        title="Yangi usta"
        footer={
          <>
            <button className="btn" onClick={() => setOpenCreate(false)}>Bekor</button>
            <button className="btn btn-primary" onClick={submit} disabled={create.isPending}>
              {create.isPending ? 'Yaratilmoqda...' : 'Yaratish'}
            </button>
          </>
        }
      >
        <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
          30 kunlik bepul sinov muddati avtomatik beriladi.
        </div>
        <div className="field">
          <label className="field-label">Usta nomi *</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="AvtoServis №1" />
        </div>
        <div className="field">
          <label className="field-label">Shahar</label>
          <input value={city} onChange={(e) => setCity(e.target.value)} />
        </div>
        <div className="field">
          <label className="field-label">Telefon</label>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+998 90 123 45 67" />
        </div>
        <div className="field">
          <label className="field-label">Manzil</label>
          <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Yangi Shahar, 15-uy" />
        </div>
      </Modal>
    </>
  );
}
